import { Box, Button, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import { MutableRefObject, useEffect, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { getLoggedInUserRole } from "../user/UserService";
import { FacultyNoGradesMessage, useFacultyHasNoGrades } from "./FacultyNoGrades";
import { ComponentName } from "./Constants";
import { STUDENT_STATUS, UserRole } from "./types";
import { getStaticGrades, getStaticSections } from "./Util";

const DEFAULT_STATUS_LIST: string[] = [
  STUDENT_STATUS.Registered,
  STUDENT_STATUS.Active,
  STUDENT_STATUS.Dropped,
  STUDENT_STATUS.Transferred,
];

interface GradeSectionComponentProps {
  submitMethod?: any;
  selectGradeRef?: MutableRefObject<HTMLSelectElement>;
  selectSectionRef?: MutableRefObject<HTMLSelectElement>;
  buttonName?: string | "Submit";
  addGraduate?: boolean;
  viewName?: string;
  message?: string;
  statusListRequired?: boolean | false;
  grade: string;
  setGrade: (grade: string) => void;
  section: string;
  setSection: (section: string) => void;
  status?: string;
  setStatus?: (status: string) => void;
  statusList?: string[];
  buttonRequired?: boolean;
  /** No outer margins - for placing the filter inside a page's top bar. */
  inline?: boolean;
}

export const GradeSectionComponent = ({
  submitMethod,
  selectGradeRef,
  selectSectionRef,
  buttonName,
  addGraduate = false,
  viewName,
  message,
  statusListRequired = false,
  grade,
  setGrade,
  section,
  setSection,
  status,
  setStatus,
  statusList = DEFAULT_STATUS_LIST,
  buttonRequired,
  inline = false
}: GradeSectionComponentProps) => {
  const [grades, setGrades] = useState<string[]>([]);
  const [sections, setSections] = useState<string[]>([]);

  const { user } = useAuth();
  const noGrades = useFacultyHasNoGrades();
  const [userRole, setUserRole] = useState<string>(getLoggedInUserRole());

  const xsSelectSize: string = "14px";
  const mdSelectSize: string = "16px";

  useEffect(() => {
    let staticGrades = getStaticGrades();
    let staticSections = getStaticSections();

    if (userRole === UserRole.FACULTY) {
      staticGrades = [...(user?.grades ?? [])];
    } else {
      if (addGraduate) {
        staticGrades.push("Graduate");
      }
      if (viewName === ComponentName.MODIFY_STUDENTS) {
        staticSections.push("NONE");
      }
    }
    setGrades(staticGrades);
    if (staticGrades.length > 0 && grade === "") setGrade(staticGrades[0])
    setSections(staticSections);
    if (staticSections.length > 0 && section === "") setSection(staticSections[0])
  }, [userRole, user, addGraduate, viewName, grade, section]);

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("In submit");
    let formData = new FormData(e.currentTarget);
    let grade = formData.get("grade")?.valueOf().toString();
    let section = formData.get("section")?.valueOf().toString();

    if (statusListRequired) {
      let status = formData.get("status")?.valueOf().toString();
      submitMethod(grade, section, grade === "Graduate" ? "true" : status);
    } else {
      submitMethod(grade, section);
    }
  };
  if (noGrades) return <FacultyNoGradesMessage />;
  return (
    <>
      <Box
        component="form"
        sx={{
          display: "flex",
          gap: 1,
          margin: inline ? 0 : "5px",
          marginLeft: inline ? 0 : { md: "60px" },
        }}
        onSubmit={submit}
      >
        {message !== undefined ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center", // vertical alignment
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: {
                  xs: 10,
                  md: mdSelectSize,
                },
              }}
            >
              {message}
            </Typography>
          </Box>
        ) : (
          ""
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            margin: inline ? 0 : "5px",
          }}
        >
          <FormControl size="small">
            <InputLabel
              id="grade-select-label"
              sx={{
                fontSize: { xs: xsSelectSize, md: mdSelectSize }, // smaller label
              }}
              shrink={true} // 👈 Force shrink to prevent overlap
            >
              Grade
            </InputLabel>
            <Select
              labelId="grade-select-label"
              id="grade-select"
              name="grade"
              label="Grade"
              value={grade}
              onChange={(e: SelectChangeEvent<string>) => setGrade(e.target.value)}
              ref={selectGradeRef}
              sx={{
                fontSize: { xs: xsSelectSize, md: mdSelectSize },
              }}
            >
              {grades?.map((grade: string) => (
                <MenuItem
                  value={grade}
                  key={grade}
                  sx={{
                    fontSize: { xs: xsSelectSize, md: mdSelectSize }, // smaller text
                  }}
                >
                  {grade}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: "80px", md: "100px" } }}>
            <InputLabel
              id="section-select-label"
              sx={{
                fontSize: { xs: xsSelectSize, md: mdSelectSize }, // smaller label
              }}
              shrink={true} // 👈 Force shrink to prevent overlap
            >
              Section
            </InputLabel>
            <Select
              labelId="section-select-label"
              id="section-select"
              name="section"
              label="Section"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              ref={selectSectionRef}
              sx={{
                fontSize: { xs: xsSelectSize, md: mdSelectSize },
              }}
            >
              {sections?.map((section: string) => (
                <MenuItem
                  value={section}
                  key={section}
                  sx={{
                    fontSize: { xs: xsSelectSize, md: mdSelectSize }, // smaller text
                  }}
                >
                  {section}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {userRole === UserRole.ADMIN && statusListRequired && grade !== "Graduate" ? (
            <FormControl sx={{ minWidth: 90 }} size="small">
              <InputLabel id="status-label">Status</InputLabel>
              <Select labelId="status-select-label" id="status-select"
                name="status"
                label="Status"
                value={status}
                defaultValue={"ACTIVE"}
                onChange={e => setStatus(e.target.value)}>
                {statusList?.map((status: string) => (
                  <MenuItem value={status} key={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            ""
          )}
          {buttonRequired && <Box
            sx={{
              display: "flex",
              alignItems: "center", // vertical alignment
            }}
          >
            <Button type="submit" variant="contained" color="primary" size="small">
              {buttonName}
            </Button>
          </Box>}

        </Box>
      </Box>
    </>
  );
};
